import { useCallback, useEffect, useState } from 'react';
import { Mark } from './Mark';
import { api, ApiError, type Me } from './api';
import { JoinStaff, SignedOut } from './Join';
import { StaffPanel } from './panels/Staff';
import { DashboardsPanel } from './panels/Dashboards';
import { ActivationPanel } from './panels/Activation';
import { OrderQueuePanel } from './panels/OrderQueue';
import { ApprovalsPanel } from './panels/Approvals';
import { VendorsPanel } from './panels/Vendors';
import { AssociationsPanel, BidsPanel } from './panels/Bids';
import { CatalogImportsPanel, CatalogSetupPanel, MastersPanel, OffersPanel } from './panels/Catalog';
import { CampsPanel } from './panels/Camps';
import { PermissionTemplatesPanel } from './panels/PermissionTemplates';
import { ReorderPanel } from './panels/Reorder';
import { AccessPanel } from './panels/Access';
import { OnboardingPanel } from './panels/Onboarding';
import { ShipToPanel } from './panels/ShipTo';
import { EdiPanel } from './panels/Edi';
import { InvoicesPanel } from './panels/Invoices';
import { InvoiceAnalysisPanel } from './panels/InvoiceAnalysis';
import { ScheduledJobsPanel } from './panels/ScheduledJobs';
import { ReportsPanel } from './panels/Reports';
import { PoDeliveryPanel } from './panels/PoDelivery';
import { OverridesPanel } from './panels/Overrides';
import { AuditPanel } from './panels/Audit';

// Every console the admin center exposes. Grouped for the nav — top-level buttons pick a group;
// a sub-nav row appears for groups with more than one console. Track F1 ports the commerce ops
// consoles in here one group at a time.
type ConsoleId =
  | 'staff'
  | 'audit'
  | 'orders'
  | 'podelivery'
  | 'overrides'
  | 'approvals'
  | 'vendors'
  | 'camps'
  | 'templates'
  | 'bids'
  | 'masters'
  | 'offers'
  | 'catalog-setup'
  | 'catalog-imports'
  | 'associations'
  | 'reorder'
  | 'edi'
  | 'invoices'
  | 'analysis'
  | 'onboarding'
  | 'shipto'
  | 'access'
  | 'activation'
  | 'dashboards'
  | 'reports'
  | 'jobs';

const NAV: { group: string; items: { id: ConsoleId; label: string }[] }[] = [
  { group: 'Staff', items: [{ id: 'staff', label: 'Directory' }, { id: 'audit', label: 'Audit log' }] },
  {
    group: 'Operations',
    items: [
      { id: 'orders', label: 'Order Queue' },
      { id: 'podelivery', label: 'PO Delivery' },
      { id: 'approvals', label: 'Approvals' },
      { id: 'overrides', label: 'Rule Overrides' },
      { id: 'vendors', label: 'Vendors' },
      { id: 'reorder', label: 'Reorder' },
      { id: 'edi', label: 'EDI' },
    ],
  },
  {
    group: 'Catalog',
    items: [
      { id: 'masters', label: 'Masters' },
      { id: 'offers', label: 'Supplier Products' },
      { id: 'bids', label: 'Bids' },
      { id: 'associations', label: 'Associations' },
      { id: 'catalog-setup', label: 'Units & Brands' },
      { id: 'catalog-imports', label: 'Imports' },
    ],
  },
  { group: 'Invoicing', items: [{ id: 'invoices', label: 'Invoices' }, { id: 'analysis', label: 'Invoice Analysis' }] },
  {
    group: 'Accounts',
    items: [
      { id: 'camps', label: 'Camps' },
      { id: 'templates', label: 'Permission Templates' },
      { id: 'onboarding', label: 'Onboarding' },
      { id: 'shipto', label: 'Ship-to' },
      { id: 'access', label: 'Access' },
      { id: 'activation', label: 'Activation' },
    ],
  },
  { group: 'Reports', items: [{ id: 'reports', label: 'Reports' }] },
  { group: 'Jobs', items: [{ id: 'jobs', label: 'Scheduled Jobs' }] },
  { group: 'Dashboards', items: [{ id: 'dashboards', label: 'Cross-stack' }] },
];

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // 401: nobody is signed in. 403: signed in, but not on the staff list (admin/src/auth.ts requireStaff).
  const [gate, setGate] = useState<'signin' | 'join' | null>(null);
  const [active, setActive] = useState<ConsoleId>('staff');

  const loadMe = useCallback(() => {
    api.me().then((m) => { setMe(m); setGate(null); }).catch((e) => {
      const status = e instanceof ApiError ? e.status : 0;
      if (status === 401) setGate('signin');
      else if (status === 403) setGate('join');
      else setErr(String(e.message ?? e));
    });
  }, []);
  useEffect(() => { loadMe(); }, [loadMe]);

  if (gate === 'signin') return <SignedOut />;
  if (gate === 'join') return <JoinStaff onJoined={loadMe} />;
  if (err) return <div className="boot err">Admin API error: {err}</div>;
  if (!me) return <div className="boot">Loading…</div>;

  const canManage = me.role === 'admin' || me.role === 'manager';
  // Commerce mutations — recording a delivery address, editing a camp or vendor record, resending a PO,
  // a person's ordering settings — are ops work (the BFF gates them on admin/ops).
  const canOps = me.role === 'admin' || me.role === 'ops';
  const activeGroup = NAV.find((g) => g.items.some((i) => i.id === active)) ?? NAV[0];
  // On a phone the nav rows scroll sideways: bring a half-hidden pill fully into view when tapped.
  const reveal = (e: { currentTarget: HTMLElement }) => e.currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest' });

  return (
    <div className="shell">
      <header className="topbar">
        <div className="brand"><Mark /> tavezio <span className="sub">Admin</span></div>
        <nav className="tabs">
          {NAV.map((g) => (
            <button key={g.group} className={activeGroup.group === g.group ? 'on' : ''} onClick={(e) => { reveal(e); setActive(g.items[0].id); }}>{g.group}</button>
          ))}
        </nav>
        <div className="who">{me.displayName} <span className={`pill role-${me.role}`}>{me.role}</span></div>
      </header>
      {activeGroup.items.length > 1 && (
        <div className="subnav" key={activeGroup.group}>
          {activeGroup.items.map((i) => (
            <button key={i.id} className={active === i.id ? 'on' : ''} onClick={(e) => { reveal(e); setActive(i.id); }}>{i.label}</button>
          ))}
        </div>
      )}
      <main className="page">
        {active === 'staff' && <StaffPanel canManage={canManage} meId={me.id} meRole={me.role} />}
        {active === 'audit' && <AuditPanel />}
        {active === 'orders' && <OrderQueuePanel canEdit={canOps} />}
        {active === 'podelivery' && <PoDeliveryPanel canOps={canOps} />}
        {active === 'overrides' && <OverridesPanel />}
        {active === 'approvals' && <ApprovalsPanel />}
        {active === 'vendors' && <VendorsPanel canEdit={canOps} />}
        {active === 'camps' && <CampsPanel canEdit={canOps} />}
        {active === 'templates' && <PermissionTemplatesPanel canEdit={canOps} />}
        {active === 'bids' && <BidsPanel canEdit={canOps} />}
        {active === 'masters' && <MastersPanel canEdit={canOps} />}
        {active === 'offers' && <OffersPanel canEdit={canOps} />}
        {active === 'associations' && <AssociationsPanel canEdit={canOps} />}
        {active === 'catalog-setup' && <CatalogSetupPanel canEdit={canOps} />}
        {active === 'catalog-imports' && <CatalogImportsPanel canEdit={canOps} />}
        {active === 'reorder' && <ReorderPanel />}
        {active === 'edi' && <EdiPanel />}
        {active === 'invoices' && <InvoicesPanel />}
        {active === 'analysis' && <InvoiceAnalysisPanel />}
        {active === 'onboarding' && <OnboardingPanel canEditShipTo={canOps} />}
        {active === 'shipto' && <ShipToPanel canEdit={canOps} />}
        {active === 'access' && <AccessPanel canOps={canOps} />}
        {active === 'activation' && <ActivationPanel />}
        {active === 'dashboards' && <DashboardsPanel />}
        {active === 'reports' && <ReportsPanel />}
        {active === 'jobs' && <ScheduledJobsPanel canRun={canOps} />}
      </main>
    </div>
  );
}
